import React, { useState, useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { 
  Phone, 
  Clock, 
  Users, 
  MessageCircle, 
  Calendar, 
  Database, 
  Languages, 
  Mic, 
  BarChart3, 
  Shield, 
  Music, 
  Zap,
  CheckCircle,
  ArrowRight,
  Star,
  Play,
  Pause,
  Volume2,
  FileText,
  Scale,
  Heart,
  Wrench,
  Briefcase,
  Building,
  Scissors,
  Home,
  DollarSign,
  Car,
  UtensilsCrossed,
  Sparkles,
  HardHat,
  Activity,
  Calculator,
  Monitor,
  Menu,
  X
} from 'lucide-react';
import { mockData } from '../data/mock.js';

const AIReceptionistLanding = () => {
  const [playingCall, setPlayingCall] = useState(null);
  const [featuredDemo, setFeaturedDemo] = useState('hero-demo'); // Track which demo is featured
  const [audioProgress, setAudioProgress] = useState({});
  const [audioDuration, setAudioDuration] = useState({});
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const audioRefs = useRef({});

  // Audio files configuration with conversation previews and themes
  const audioFiles = {
    'hero-demo': {
      url: 'https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/law-firm-prospect_osnnmq.mp3',
      title: 'Law Office Demo',
      subtitle: 'New client consultation',
      icon: <Scale className="w-8 h-8 text-white" />,
      theme: {
        primary: 'from-[#fa4529] to-[#ff6b3d]',
        secondary: 'from-[#fa4529]/20 to-[#ff6b3d]/20',
        border: 'border-[#fa4529]',
        shadow: 'shadow-[#fa4529]/20',
        bg: 'from-orange-600/40 to-orange-800/40',
        borderActive: 'border-orange-400'
      },
      conversation: [
        { speaker: 'AI', text: "Hello, thank you for calling the Law Offices of Abram and Owens. My name is Grace. How could I assist you today?" },
        { speaker: 'Caller', text: "Hi Grace, I'm looking to get some information for incorporating a business..." }
      ]
    },
    'dental-quick': {
      url: 'https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/dental-appointment-scheduling_pf5fvb.mp3',
      title: 'Dental Practice',
      subtitle: 'Appointment scheduling',
      icon: <Heart className="w-8 h-8 text-white" />,
      theme: {
        primary: 'from-blue-500 to-blue-600',
        secondary: 'from-blue-500/20 to-blue-600/20',
        border: 'border-blue-500',
        shadow: 'shadow-blue-500/20',
        bg: 'from-blue-600/40 to-blue-800/40',
        borderActive: 'border-blue-400'
      },
      conversation: [
        { speaker: 'AI', text: "Hello, thank you for calling Smile Bright. My name is Grace. How could I assist you today?" },
        { speaker: 'Caller', text: "Hi, I wanted to make an appointment. One of my teeth started hurting recently." }
      ]
    },
    'hvac-quick': {
      url: 'https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/hvac-scheduling_uwmoew.mp3',
      title: 'HVAC Service',
      subtitle: 'Emergency scheduling',
      icon: <Zap className="w-8 h-8 text-white" />,
      theme: {
        primary: 'from-red-500 to-red-600',
        secondary: 'from-red-500/20 to-red-600/20',
        border: 'border-red-500',
        shadow: 'shadow-red-500/20',
        bg: 'from-red-600/40 to-red-800/40',
        borderActive: 'border-red-400'
      },
      conversation: [
        { speaker: 'AI', text: "Hello, thank you for calling Sunnyside Air Conditioning. My name is Christina. How could I help you today?" },
        { speaker: 'Caller', text: "Hi, one of my units started leaking recently." }
      ]
    }
  };

  // Initialize audio elements
  useEffect(() => {
    // Initialize hero demo audio elements
    Object.keys(audioFiles).forEach(key => {
      if (!audioRefs.current[key]) {
        const audio = new Audio(audioFiles[key].url);
        audioRefs.current[key] = audio;

        // Set up event listeners
        audio.addEventListener('loadedmetadata', () => {
          setAudioDuration(prev => ({
            ...prev,
            [key]: audio.duration
          }));
        });

        audio.addEventListener('timeupdate', () => {
          setAudioProgress(prev => ({
            ...prev,
            [key]: (audio.currentTime / audio.duration) * 100
          }));
        });

        audio.addEventListener('ended', () => {
          setPlayingCall(null);
          setAudioProgress(prev => ({
            ...prev,
            [key]: 0
          }));
        });
      }
    });

    // Initialize sample calls audio elements
    sampleCalls.forEach(call => {
      const callKey = `sample-${call.id}`;
      if (!audioRefs.current[callKey]) {
        const audio = new Audio(call.audioUrl);
        audioRefs.current[callKey] = audio;

        // Set up event listeners for sample calls
        audio.addEventListener('loadedmetadata', () => {
          setAudioDuration(prev => ({
            ...prev,
            [callKey]: audio.duration
          }));
        });

        audio.addEventListener('timeupdate', () => {
          setAudioProgress(prev => ({
            ...prev,
            [callKey]: (audio.currentTime / audio.duration) * 100
          }));
        });

        audio.addEventListener('ended', () => {
          setPlayingCall(null);
          setAudioProgress(prev => ({
            ...prev,
            [callKey]: 0
          }));
        });
      }
    });

    // Cleanup
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
    };
  }, []);

  // Back to top functionality
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const handleBookDemo = () => {
    // Open email client with demo request
    window.location.href = 'https://outlook.office.com/book/ScheduleaDemo@ghostengr.com/?ismsaljsauthenabled';
  };

  const handleContactForm = (e) => {
    e.preventDefault();
    // Simple contact form handling
    const formData = new FormData(e.target);
    const email = formData.get('email');
    const name = formData.get('name') || 'Prospect';
    
    window.location.href = `mailto:contact@ghostengr.com?subject=AI Receptionist Demo Request from ${name}&body=Hi, I would like to schedule a demo of the AI Receptionist service.%0A%0AEmail: ${email}%0A%0APlease let me know your availability.`;
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleMobileNavClick = (targetId) => {
    setIsMobileMenuOpen(false);
    if (targetId === 'demo') {
      handleBookDemo();
    } else {
      document.getElementById(targetId)?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const sampleCalls = [
    {
      id: 1,
      businessType: "Law Office",
      scenario: "Business Incorporation Inquiry",
      duration: "1:55",
      audioUrl: "https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/law-firm-prospect_osnnmq.mp3",
      transcript: [
        { speaker: "AI", text: "Hello, thank you for calling the law offices of Abrams and Owens. My name is Grace. How can I assist you today?" },
        { speaker: "Caller", text: "I'm looking to get some information for incorporating a business. I'm looking for some legal advice, how to properly incorporate a business, set up some partners, the whole process here." },
        { speaker: "AI", text: "I'd be happy to take down some information so that an attorney can review it and determine the best way to assist you. May I have your full name please?" },
        { speaker: "Caller", text: "Yes, my name is John Smith." },
        { speaker: "AI", text: "Thank you Mr. Smith. And what's the best phone number for you in case the attorney needs to reach you or we get disconnected?" },
        { speaker: "Caller", text: "You can call me at 201-828-0632." },
        { speaker: "AI", text: "Thank you, Mr. Smith. Could you briefly describe your situation or any specific questions you have about incorporating your business and setting up partners? This will help me share the right details with the attorney." },
        { speaker: "Caller", text: "I'm just looking for some general guidance on the process and actually doing the incorporation plus setting up the contracts and legal documents for partners." },
        { speaker: "AI", text: "Thank you for sharing those details, Mr. Smith. I've noted that you're seeking general guidance on the incorporation process as well as assistance with contracts and legal documents for partners. Someone from our office will review your information and reach out to you as soon as possible." }
      ]
    },
    {
      id: 2,
      businessType: "Medical Practice",
      scenario: "Dental Emergency Appointment",
      duration: "1:55",
      audioUrl: "https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/dental-appointment-scheduling_pf5fvb.mp3",
      transcript: [
        { speaker: "AI", text: "Hello, thank you for calling SmileBright. My name is Grace. How can I assist you today?" },
        { speaker: "Caller", text: "One of my teeth started hurting recently." },
        { speaker: "AI", text: "I'm sorry to hear you're experiencing discomfort. I'll make sure our team prioritizes your request. Can I get your full name please in case we get disconnected?" },
        { speaker: "Caller", text: "Yep, it's Christina Jones." },
        { speaker: "AI", text: "Thank you, Christina. Could I also get the best phone number to reach you just in case we need to follow up?" },
        { speaker: "Caller", text: "It's 917-559-8211." },
        { speaker: "AI", text: "Thank you, Christina. Just to make sure our dentist is prepared, could you give me a brief description of what's going on with your tooth? For example, is it a sharp pain, sensitivity to hot or cold, or swelling?" },
        { speaker: "Caller", text: "Yeah, it started happening a few days ago, and it's mostly just sensitive to cold." },
        { speaker: "AI", text: "Thank you for sharing that, Christina. I'll mark this as an urgent concern so our team can prioritize your call back. Someone from Smile Bright will reach out as soon as possible to get you scheduled and discuss next steps." }
      ]
    },
    {
      id: 3,
      businessType: "HVAC Service",
      scenario: "Unit Repair Scheduling",
      duration: "1:55",
      audioUrl: "https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/hvac-scheduling_uwmoew.mp3",
      transcript: [
        { speaker: "AI", text: "Hello, thank you for calling Sunnyside Air Conditioning. My name is Christina. How can I help you today?" },
        { speaker: "Caller", text: "Hi, one of my units started leaking recently." },
        { speaker: "AI", text: "I'm sorry to hear you're having trouble with your unit. I can help get this started for you. May I please get your full name?" },
        { speaker: "Caller", text: "Yeah, it's Samantha Brown." },
        { speaker: "AI", text: "Thank you, Samantha. Could I also get the best phone number to reach you in case our technician needs to follow up?" },
        { speaker: "Caller", text: "570-872-9128." },
        { speaker: "AI", text: "Just in case you'd prefer follow-up by email or want service confirmations, may I also have your email address?" },
        { speaker: "Caller", text: "It's throwaway12@gmail.com." },
        { speaker: "AI", text: "Just to make sure we get you the right help, is this for your home or a business location?" },
        { speaker: "Caller", text: "My home." },
        { speaker: "AI", text: "Thank you. And just to confirm, are you looking for a repair service for the leaking unit or do you need something else like a maintenance check or a new system quote?" },
        { speaker: "Caller", text: "I don't really think I need a quote, but I just want someone to take a look at the unit." }
      ]
    },
    {
      id: 4,
      businessType: "Restaurant",
      scenario: "Pickup Order Processing",
      duration: "1:55",
      audioUrl: "https://res.cloudinary.com/do6trhm93/video/upload/v1753447049/restaurant-pickup_d6rxqp.mp3",
      transcript: [
        { speaker: "AI", text: "Hi, thanks for calling Vinny's Trattoria. My name is Andrew. How can I help you today?" },
        { speaker: "Caller", text: "Hi, I'd like to place an order for a pickup." },
        { speaker: "AI", text: "Can I get your name to start the order?" },
        { speaker: "Caller", text: "Yes, it's David." },
        { speaker: "AI", text: "Thanks, David. And what's the best phone number to reach you at?" },
        { speaker: "Caller", text: "917-243-2006." },
        { speaker: "AI", text: "Perfect, thanks. What would you like to order for pickup today?" },
        { speaker: "Caller", text: "I'll do a large pepperoni pie and a large diet coke." },
        { speaker: "AI", text: "Great choice, David. Your order is one large pepperoni pie and a large diet coke. You can pick it up in about 25 minutes. The total comes to $28. Anything else I can add for you?" },
        { speaker: "Caller", text: "No, that's it. Thank you." },
        { speaker: "AI", text: "You got it, David. Thanks for your order. See you soon. Have a great day." }
      ]
    }
  ];

  const togglePlayback = (callId) => {
    // Determine if this is a hero demo or sample call
    const isHeroDemo = Object.keys(audioFiles).includes(callId);
    const audioKey = isHeroDemo ? callId : `sample-${callId}`;
    const audio = audioRefs.current[audioKey];
    
    if (!audio) return;

    // For hero demos, set as featured demo
    if (isHeroDemo) {
      setFeaturedDemo(callId);
    }

    if (playingCall === (isHeroDemo ? callId : `sample-${callId}`)) {
      // Pause current audio
      audio.pause();
      setPlayingCall(null);
    } else {
      // Pause all other audio first
      Object.values(audioRefs.current).forEach(otherAudio => {
        if (otherAudio && otherAudio !== audio) {
          otherAudio.pause();
        }
      });
      
      // Play selected audio
      audio.play().catch(error => {
        console.error('Error playing audio:', error);
      });
      setPlayingCall(isHeroDemo ? callId : `sample-${callId}`);
    }
  };

  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCurrentTime = (callId) => {
    const audio = audioRefs.current[callId];
    return audio ? audio.currentTime : 0;
  };

  // Handle progress bar click to seek to position
  const handleProgressClick = (callId, event) => {
    const audio = audioRefs.current[callId];
    const durationKey = callId;
    
    if (!audio || !audioDuration[durationKey] || !isFinite(audioDuration[durationKey])) return;

    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const progressBarWidth = rect.width;
    const clickRatio = clickX / progressBarWidth;
    
    // Ensure ratio is between 0 and 1
    const clampedRatio = Math.max(0, Math.min(1, clickRatio));
    const newTime = clampedRatio * audioDuration[durationKey];
    
    // Only set currentTime if the new time is valid and finite
    if (isFinite(newTime) && newTime >= 0 && newTime <= audioDuration[durationKey]) {
      audio.currentTime = newTime;
    }
  };

  // Handle progress bar drag functionality
  const handleProgressDrag = (callId, event) => {
    const audio = audioRefs.current[callId];
    const durationKey = callId;
    
    if (!audio || !audioDuration[durationKey] || !isFinite(audioDuration[durationKey])) return;

    // Store reference to the initial progress bar element
    const initialProgressBar = event.currentTarget;

    const updateTimeFromEvent = (e) => {
      // Use the stored initial progress bar reference or query by data attribute
      const progressBar = initialProgressBar || document.querySelector(`[data-demo-id="${callId}"]`);
      if (!progressBar) return;

      const rect = progressBar.getBoundingClientRect();
      const dragX = (e.clientX || e.touches?.[0]?.clientX || 0) - rect.left;
      const progressBarWidth = rect.width;
      const dragRatio = dragX / progressBarWidth;
      
      const clampedRatio = Math.max(0, Math.min(1, dragRatio));
      const newTime = clampedRatio * audioDuration[durationKey];
      
      // Only set currentTime if the new time is valid and finite
      if (isFinite(newTime) && newTime >= 0 && newTime <= audioDuration[durationKey]) {
        audio.currentTime = newTime;
      }
    };

    const handleMouseMove = (e) => updateTimeFromEvent(e);
    const handleTouchMove = (e) => {
      e.preventDefault();
      updateTimeFromEvent(e);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleEnd);
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
    document.body.style.userSelect = 'none';

    // Also update immediately for the initial drag
    updateTimeFromEvent(event);
  };

  // Get the two non-featured demos for the quick buttons
  const getQuickDemos = () => {
    const allDemos = Object.keys(audioFiles);
    return allDemos.filter(demo => demo !== featuredDemo);
  };

  const features = [
    {
      icon: <Users className="w-6 h-6" />,
      title: "Trained Specifically to Your Business",
      description: "Our AI learns your business inside and out, providing personalized responses that sound authentically you."
    },
    {
      icon: <Phone className="w-6 h-6" />,
      title: "Warm Call Transfers",
      description: "Seamlessly transfer calls to real people when needed with complete context and warm handoffs."
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Lead Capture Excellence",
      description: "Never miss a customer's call again. We get you leads fast with intelligent qualification and routing."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Full Appointment Scheduling",
      description: "Complete scheduling capability integrated with your existing calendar systems."
    },
    {
      icon: <Database className="w-6 h-6" />,
      title: "Knowledge Base Integration",
      description: "Feed existing knowledge bases or create new ones tailored to your business needs."
    },
    {
      icon: <Languages className="w-6 h-6" />,
      title: "Multilingual Support",
      description: "Serve customers in their preferred language with natural conversation flow."
    },
    {
      icon: <Mic className="w-6 h-6" />,
      title: "Customizable Voice & Tone",
      description: "Choose from different voices and tones that match your brand personality."
    },
    {
      icon: <BarChart3 className="w-6 h-6" />,
      title: "Post-Call Analysis",
      description: "Detailed summaries and data routable to any CMS for comprehensive insights."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Spam Filtering",
      description: "Protect your team from unnecessary calls and mitigate spam risks automatically."
    },
    {
      icon: <Music className="w-6 h-6" />,
      title: "Custom Hold Music",
      description: "Personalize the caller experience with your choice of hold music and messaging."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "24/7 Availability",
      description: "Always-on professional presence that never sleeps, never takes a break."
    },
    {
      icon: <MessageCircle className="w-6 h-6" />,
      title: "Smart Automation",
      description: "Custom triggers and automated workflows to notify your team about leads and calls."
    },
    {
      icon: <Users className="w-6 h-6" />,
      title: "High Capacity Handling",
      description: "Handle up to 20 simultaneous calls without missing a beat."
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      title: "Solo Marketing Consultant",
      content: "Ghost Engineering's AI Receptionist has transformed how I handle client calls. I never miss an opportunity now, and the lead qualification is spot-on.",
      rating: 5
    },
    {
      name: "Dr. Michael Chen",
      title: "Medical Practice Owner",
      content: "The 24/7 availability and appointment scheduling have been game-changers for our practice. Patients love the seamless experience.",
      rating: 5
    },
    {
      name: "Lisa Rodriguez",
      title: "HVAC Service Company",
      content: "Emergency calls are handled perfectly, and the warm transfers to our technicians are flawless. Our response time has improved dramatically.",
      rating: 5
    }
  ];

  const useCases = [
    {
      title: "Law Offices",
      description: "Professional intake, appointment booking, and lead qualification for legal practices.",
      icon: <Scale className="w-6 h-6" />
    },
    {
      title: "Medical & Wellness",
      description: "HIPAA-compliant call handling, appointment scheduling, and patient communication.",
      icon: <Heart className="w-6 h-6" />
    },
    {
      title: "Home Services",
      description: "24/7 emergency support, intelligent routing, and service scheduling for HVAC, plumbing, electrical.",
      icon: <Wrench className="w-6 h-6" />
    },
    {
      title: "Agencies & Consultants",
      description: "Professional presence, lead qualification, and seamless client communication.",
      icon: <Briefcase className="w-6 h-6" />
    },
    {
      title: "Property Management",
      description: "Tenant inquiries, maintenance requests, and property showing coordination.",
      icon: <Building className="w-6 h-6" />
    },
    {
      title: "Salons & Spas",
      description: "Appointment booking, service inquiries, and customer support automation.",
      icon: <Scissors className="w-6 h-6" />
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md border-b border-gray-200/50 sticky top-0 z-50 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img 
                src="https://res.cloudinary.com/do6trhm93/image/upload/v1752670263/ghost-ai-logo_kpobg0.png" 
                alt="Ghost AI" 
                className="w-[150px] h-auto"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-[#fa4529] transition-colors duration-300 font-medium">Features</a>
              <a href="#pricing" className="text-gray-600 hover:text-[#fa4529] transition-colors duration-300 font-medium">Pricing</a>
              <a href="#testimonials" className="text-gray-600 hover:text-[#fa4529] transition-colors duration-300 font-medium">Testimonials</a>
              <Button onClick={handleBookDemo} className="bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] hover:from-[#e03d24] hover:to-[#fa4529] text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5">
                Book a Demo
              </Button>
            </nav>

            {/* Mobile Menu Button */}
            <button 
              onClick={toggleMobileMenu}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-600" />
              ) : (
                <Menu className="w-6 h-6 text-gray-600" />
              )}
            </button>
          </div>

          {/* Mobile Navigation Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden mt-4 pb-4 border-t border-gray-200/50">
              <nav className="flex flex-col space-y-4 pt-4">
                <button 
                  onClick={() => handleMobileNavClick('features')}
                  className="text-left text-gray-600 hover:text-[#fa4529] transition-colors duration-300 font-medium py-2 px-2 rounded-lg hover:bg-gray-50"
                >
                  Features
                </button>
                <button 
                  onClick={() => handleMobileNavClick('pricing')}
                  className="text-left text-gray-600 hover:text-[#fa4529] transition-colors duration-300 font-medium py-2 px-2 rounded-lg hover:bg-gray-50"
                >
                  Pricing
                </button>
                <button 
                  onClick={() => handleMobileNavClick('testimonials')}
                  className="text-left text-gray-600 hover:text-[#fa4529] transition-colors duration-300 font-medium py-2 px-2 rounded-lg hover:bg-gray-50"
                >
                  Testimonials
                </button>
                <Button 
                  onClick={() => handleMobileNavClick('demo')}
                  className="bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] hover:from-[#e03d24] hover:to-[#fa4529] text-white shadow-lg hover:shadow-xl transition-all duration-300 w-full mt-2"
                >
                  Book a Demo
                </Button>
              </nav>
            </div>
          )}
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-20 md:py-32 lg:py-40 bg-gradient-to-br from-black via-gray-900 to-black overflow-hidden">
        {/* Dynamic geometric shapes */}
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-32 h-32 bg-gradient-to-br from-[#fa4529] to-[#ff6b3d] rounded-full blur-xl opacity-30 animate-pulse"></div>
          <div className="absolute top-40 right-20 w-64 h-64 bg-gradient-to-tl from-[#fa4529]/20 to-transparent rounded-full blur-2xl"></div>
          <div className="absolute bottom-20 left-1/3 w-48 h-48 bg-gradient-to-r from-[#ff6b3d] to-[#fa4529] rounded-full blur-3xl opacity-20"></div>
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#fa4529]/30 via-[#ff6b3d]/10 to-transparent rounded-full blur-3xl"></div>
          
          {/* Animated grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width=%2260%22%20height=%2260%22%20viewBox=%220%200%2060%2060%22%20xmlns=%22http://www.w3.org/2000/svg%22%3E%3Cg%20fill=%22none%22%20fill-rule=%22evenodd%22%3E%3Cg%20fill=%22%23fa4529%22%20fill-opacity=%220.05%22%3E%3Ccircle%20cx=%2230%22%20cy=%2230%22%20r=%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')] opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center">
            {/* Left Column - Hero Content */}
            <div className="text-center lg:text-left space-y-6 md:space-y-8 lg:space-y-10">
              <div className="relative">
                <Badge className="mb-4 md:mb-6 lg:mb-8 bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] text-white border-0 px-4 md:px-6 py-2 md:py-3 text-xs md:text-sm font-bold uppercase tracking-wider shadow-2xl">
                  <span className="relative z-10">Never Miss Another Call</span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b3d] to-[#fa4529] rounded-full blur-lg opacity-50"></div>
                </Badge>
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl xl:text-6xl 2xl:text-7xl font-black text-white leading-[0.95] tracking-tight">
                <span className="block bg-gradient-to-r from-[#fa4529] via-[#ff6b3d] to-[#fa4529] bg-clip-text text-transparent">
                  TRANSFORM
                </span>
                <span className="block text-3xl md:text-4xl lg:text-5xl xl:text-6xl mt-2 text-gray-300">
                  Every Call into
                </span>
                <span className="block text-4xl md:text-5xl lg:text-6xl xl:text-7xl bg-gradient-to-r from-[#fa4529] via-[#ff6b3d] to-[#fa4529] bg-clip-text text-transparent animate-pulse" style={{animationDuration: '3s'}}>
                  OPPORTUNITY
                </span>
              </h1>
              
              <p className="text-lg text-gray-400 font-semibold uppercase tracking-wider mt-6 mb-4">
                AI Voice Assistant
              </p>
              
              <p className="text-xl text-gray-300 leading-relaxed max-w-2xl font-light">
                Professional AI receptionist handling, lead capture, and appointment scheduling for ambitious businesses. 
                <span className="text-[#fa4529] font-semibold"> Never miss a customer's call again.</span>
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center lg:justify-start pt-8">
                <Button size="lg" onClick={handleBookDemo} className="relative bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] text-white px-10 py-8 text-xl font-bold shadow-2xl hover:shadow-[#fa4529]/50 transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 border-0 rounded-2xl group overflow-hidden">
                  <span className="relative z-10 flex items-center">
                    Book a Demo
                    <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                  <div className="absolute inset-0 bg-gradient-to-r from-[#ff6b3d] to-[#fa4529] opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Button>
                
                <Button size="lg" variant="outline" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })} className="border-3 border-[#fa4529] text-[#fa4529] hover:bg-[#fa4529] hover:text-white px-10 py-8 text-xl font-bold transition-all duration-500 transform hover:-translate-y-2 hover:scale-105 rounded-2xl backdrop-blur-sm bg-white/10">
                  View Features
                </Button>
              </div>
            </div>

            {/* Right Column - Interactive Demo */}
            <div className="relative">
              {/* Removed bouncing orange box */}
              <div className="absolute -bottom-10 -left-10 w-16 h-16 bg-gradient-to-tl from-[#ff6b3d] to-[#fa4529] rounded-full opacity-60 animate-pulse" style={{animationDuration: '3s'}}></div>
              
              <div className="bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-xl rounded-3xl p-4 md:p-6 lg:p-10 border border-white/20 shadow-2xl transform hover:scale-105 transition-all duration-500">
                <div className="text-center mb-6 md:mb-8 lg:mb-10">
                  <div className="inline-block p-3 md:p-4 bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] rounded-2xl mb-3 md:mb-4 shadow-2xl">
                    <Phone className="w-6 h-6 md:w-8 md:h-8 text-white" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 md:mb-3">Experience the Future</h3>
                  <p className="text-gray-300 text-base md:text-lg">Live AI demonstration</p>
                </div>

                {/* Interactive Demo Panel */}
                <div className="space-y-4 md:space-y-6">
                  <div className={`bg-gradient-to-r from-black/50 to-gray-900/50 rounded-2xl p-3 md:p-4 lg:p-6 border transition-all duration-500 ${
                  audioFiles[featuredDemo].theme.border
                }/40 shadow-lg ${audioFiles[featuredDemo].theme.shadow}`}>
                    <div className="flex items-center justify-between mb-4 md:mb-6">
                      <div className="flex items-center space-x-3 md:space-x-4">
                        <div className={`w-12 h-12 md:w-16 md:h-16 bg-gradient-to-br ${audioFiles[featuredDemo].theme.primary} rounded-2xl flex items-center justify-center shadow-2xl animate-pulse transition-all duration-500`} style={{animationDuration: '3s'}}>
                          {audioFiles[featuredDemo].icon}
                        </div>
                        <div>
                          <h4 className="font-bold text-white text-lg md:text-xl">{audioFiles[featuredDemo].title}</h4>
                          <p className="text-gray-400 text-sm md:text-base">{audioFiles[featuredDemo].subtitle}</p>
                        </div>
                      </div>
                      <Badge className={`bg-gradient-to-r ${audioFiles[featuredDemo].theme.primary} text-white px-2 md:px-4 py-1 md:py-2 font-bold transition-all duration-500 text-xs md:text-sm`}>
                        {formatTime(audioDuration[featuredDemo] || 0)}
                      </Badge>
                    </div>
                    
                    {/* Enhanced Audio Player */}
                    <div className="bg-gradient-to-r from-white/10 to-white/5 rounded-xl p-3 md:p-4 lg:p-6 backdrop-blur-sm border border-white/10">
                      <div className="flex items-center space-x-4 mb-4">
                        <button
                          onClick={() => togglePlayback(featuredDemo)}
                          className={`w-16 h-16 bg-gradient-to-br ${audioFiles[featuredDemo].theme.primary} rounded-full flex items-center justify-center shadow-2xl hover:shadow-xl transition-all duration-300 transform hover:scale-110 group`}
                        >
                          {playingCall === featuredDemo ? (
                            <Pause className="w-8 h-8 text-white" />
                          ) : (
                            <Play className="w-8 h-8 text-white ml-1" />
                          )}
                        </button>
                        <div className="flex-1">
                          <div 
                            className="progress-bar-container w-full bg-gray-700 rounded-full h-4 shadow-inner cursor-pointer hover:bg-gray-600 transition-colors duration-200"
                            data-demo-id={featuredDemo}
                            onClick={(e) => handleProgressClick(featuredDemo, e)}
                            onMouseDown={(e) => handleProgressDrag(featuredDemo, e)}
                            onTouchStart={(e) => handleProgressDrag(featuredDemo, e)}
                          >
                            <div 
                              className={`bg-gradient-to-r ${audioFiles[featuredDemo].theme.primary} h-4 rounded-full transition-all duration-300 shadow-lg relative`}
                              style={{ width: `${audioProgress[featuredDemo] || 0}%` }}
                            >
                              {/* Draggable handle */}
                              <div 
                                className={`absolute right-0 top-1/2 transform -translate-y-1/2 w-6 h-6 bg-white ${audioFiles[featuredDemo].theme.border} border-2 rounded-full shadow-lg opacity-0 hover:opacity-100 transition-opacity duration-200 cursor-grab active:cursor-grabbing`}
                                style={{
                                  right: '-12px',
                                  opacity: audioProgress[featuredDemo] > 0 ? 0.8 : 0
                                }}
                              />
                            </div>
                          </div>
                          <div className="text-sm text-gray-300 mt-2 font-mono">
                            {formatTime(getCurrentTime(featuredDemo))} / {formatTime(audioDuration[featuredDemo] || 0)}
                          </div>
                        </div>
                        <Volume2 className="w-6 h-6 text-gray-400" />
                      </div>
                    </div>

                    {/* Animated conversation preview */}
                    <div className="mt-6 space-y-4">
                      {audioFiles[featuredDemo].conversation.map((line, index) => (
                        <div key={index} className={`rounded-xl p-4 backdrop-blur-sm transition-all duration-500 ${
                          line.speaker === 'AI' 
                            ? `bg-gradient-to-r ${audioFiles[featuredDemo].theme.secondary} border-l-4 ${audioFiles[featuredDemo].theme.border}` 
                            : 'bg-gradient-to-r from-gray-700/50 to-gray-600/50 border-l-4 border-gray-500'
                        }`}>
                          <div className="flex items-start space-x-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                              line.speaker === 'AI'
                                ? `bg-gradient-to-br ${audioFiles[featuredDemo].theme.primary}`
                                : 'bg-gray-500'
                            }`}>
                              {line.speaker === 'AI' ? (
                                <span className="text-white font-bold text-xs">AI</span>
                              ) : (
                                <Users className="w-4 h-4 text-white" />
                              )}
                            </div>
                            <p className={`text-sm leading-relaxed ${
                              line.speaker === 'AI' ? 'text-white' : 'text-gray-300'
                            }`}>
                              "{line.text}"
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Quick demo buttons - dynamically show non-featured demos */}
                  <div className="grid grid-cols-2 gap-4">
                    {getQuickDemos().map((demoId, index) => (
                      <button 
                        key={demoId}
                        onClick={() => togglePlayback(demoId)}
                        className={`group rounded-xl p-4 border transition-all duration-500 transform hover:scale-105 backdrop-blur-sm bg-gradient-to-br ${audioFiles[demoId].theme.bg} ${audioFiles[demoId].theme.borderActive} shadow-lg ${audioFiles[demoId].theme.shadow}`}
                      >
                        <div className="flex items-center space-x-3">
                          <div className={`w-10 h-10 bg-gradient-to-br ${audioFiles[demoId].theme.primary} rounded-xl flex items-center justify-center shadow-lg transition-all duration-300`}>
                            {demoId === 'hero-demo' ? <Scale className="w-5 h-5 text-white" /> : 
                             demoId === 'dental-quick' ? <Heart className="w-5 h-5 text-white" /> : 
                             <Zap className="w-5 h-5 text-white" />}
                          </div>
                          <div className="text-left">
                            <div className="font-bold text-white text-sm">{audioFiles[demoId].title}</div>
                            <div className="text-xs text-gray-300">{formatTime(audioDuration[demoId] || 0)}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Dramatic Stats Row */}
          <div className="mt-32 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-[#fa4529]/10 via-transparent to-[#ff6b3d]/10 rounded-3xl blur-xl"></div>
            <div className="relative bg-gradient-to-r from-white/10 to-white/5 backdrop-blur-xl rounded-3xl p-12 border border-white/20 shadow-2xl">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12">
                <div className="text-center group cursor-pointer">
                  <div className="text-6xl font-black bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] bg-clip-text text-transparent mb-4 group-hover:scale-110 transition-all duration-500">
                    24/7
                  </div>
                  <div className="text-xl font-semibold text-gray-300 mb-2">Always Available</div>
                  <div className="text-gray-400">Never miss another opportunity</div>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="text-6xl font-black bg-gradient-to-r from-[#ff6b3d] to-[#fa4529] bg-clip-text text-transparent mb-4 group-hover:scale-110 transition-all duration-500">
                    20x
                  </div>
                  <div className="text-xl font-semibold text-gray-300 mb-2">Simultaneous Calls</div>
                  <div className="text-gray-400">Handle peak volumes effortlessly</div>
                </div>
                <div className="text-center group cursor-pointer">
                  <div className="text-6xl font-black bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] bg-clip-text text-transparent mb-4 group-hover:scale-110 transition-all duration-500">
                    98%
                  </div>
                  <div className="text-xl font-semibold text-gray-300 mb-2">Lead Capture Rate</div>
                  <div className="text-gray-400">Maximum conversion guaranteed</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Powerful Features Built for Growth
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Everything you need to handle calls professionally, capture leads effectively, and grow your business faster.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-4">
                    {feature.icon}
                  </div>
                  <CardTitle className="text-xl text-gray-900">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base">
                    {feature.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sample Calls Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Hear Our AI in Action
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Listen to real conversations between our AI and customers across different industries. 
              Experience the natural flow and professional handling.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {sampleCalls.map((call) => (
              <Card key={call.id} className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-xl text-gray-900">{call.businessType}</CardTitle>
                      <CardDescription className="text-gray-600">{call.scenario}</CardDescription>
                    </div>
                    <Badge variant="secondary" className="bg-gray-100 text-gray-700">
                      {call.duration}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {/* Audio Player with Progress Bar */}
                  <div className="mb-6">
                    <div className="bg-gray-100 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <button
                          onClick={() => togglePlayback(call.id)}
                          className="w-10 h-10 bg-black text-white rounded-full flex items-center justify-center hover:bg-gray-800 transition-colors"
                        >
                          {playingCall === `sample-${call.id}` ? (
                            <Pause className="w-5 h-5" />
                          ) : (
                            <Play className="w-5 h-5 ml-1" />
                          )}
                        </button>
                        <div className="flex-1 mx-3">
                          {/* Always visible progress track */}
                          <div className="mb-2">
                            <div 
                              className="progress-bar-container w-full bg-gray-400 rounded-full h-2 cursor-pointer hover:bg-gray-500 transition-colors duration-200 border border-gray-500 shadow-sm"
                              data-demo-id={`sample-${call.id}`}
                              onClick={(e) => handleProgressClick(`sample-${call.id}`, e)}
                              onMouseDown={(e) => handleProgressDrag(`sample-${call.id}`, e)}
                              onTouchStart={(e) => handleProgressDrag(`sample-${call.id}`, e)}
                            >
                              <div 
                                className="bg-black h-2 rounded-full transition-all duration-300 relative"
                                style={{ 
                                  width: `${Math.max(audioProgress[`sample-${call.id}`] || 0, 0)}%`
                                }}
                              >
                                {/* Draggable handle - always show when progress > 0 */}
                                {audioProgress[`sample-${call.id}`] > 0 && (
                                  <div 
                                    className="absolute right-0 top-1/2 transform -translate-y-1/2 w-3 h-3 bg-white border border-black rounded-full shadow cursor-grab active:cursor-grabbing"
                                    style={{ right: '-6px' }}
                                  />
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        <Volume2 className="w-5 h-5 text-gray-500" />
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 text-center font-mono">
                      {playingCall === `sample-${call.id}` ? 
                        `${formatTime(getCurrentTime(`sample-${call.id}`))} / ${formatTime(audioDuration[`sample-${call.id}`] || 0)}` : 
                        'Click to play sample call'
                      }
                    </div>
                  </div>

                  {/* Call Transcript */}
                  <div className="space-y-3">
                    <div className="flex items-center space-x-2 text-sm text-gray-700 mb-3">
                      <FileText className="w-4 h-4" />
                      <span>Call Transcript</span>
                    </div>
                    <div className="max-h-48 overflow-y-auto bg-gray-50 rounded-lg p-3 space-y-2">
                      {call.transcript.map((line, index) => (
                        <div key={index} className="flex space-x-2">
                          <span className={`font-medium text-sm ${
                            line.speaker === 'AI' ? 'text-black' : 'text-gray-600'
                          }`}>
                            {line.speaker}:
                          </span>
                          <span className="text-sm text-gray-700">{line.text}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Call Features Highlighted */}
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <div className="text-sm text-green-800">
                      <strong>Features Demonstrated:</strong> 
                      {call.businessType === 'Law Office' && ' Professional intake, lead qualification, appointment scheduling, client information capture'}
                      {call.businessType === 'Medical Practice' && ' Patient scheduling, symptom assessment, priority triage, follow-up coordination'}
                      {call.businessType === 'HVAC Service' && ' Service scheduling, problem diagnosis, contact information capture, priority assessment'}
                      {call.businessType === 'Restaurant' && ' Order taking, customer information capture, pickup coordination, payment processing'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center mt-12">
            <Button onClick={handleBookDemo} className="bg-black text-white hover:bg-gray-800">
              Request a Demo
            </Button>
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Perfect for Your Industry
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored solutions for service-based businesses, professional firms, and solo practitioners.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {useCases.map((useCase, index) => (
              <Card key={index} className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-12 h-12 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-4">
                    {useCase.icon}
                  </div>
                  <CardTitle className="text-xl text-gray-900">{useCase.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-base">
                    {useCase.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Industries */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              And Many More Industries...
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Home className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Real Estate</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Property inquiries, showing coordination, and lead qualification for agents and brokers.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <DollarSign className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Financial Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Client screening, appointment scheduling, and initial consultation coordination.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-orange-600 text-white rounded-lg flex items-center justify-center mb-3">
                    <Heart className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Veterinary Clinics</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Appointment booking, emergency triage, and pet care inquiries.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Car className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Auto Repair</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Service scheduling, estimate requests, and customer communication.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <UtensilsCrossed className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Restaurants</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Reservation management, takeout orders, and customer inquiries.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Cleaning Services</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Booking coordination, service customization, and client communication.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <HardHat className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Construction</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Project inquiries, estimate scheduling, and contractor coordination.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Shield className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Insurance</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Claims processing, policy inquiries, and agent appointment scheduling.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-orange-600 text-white rounded-lg flex items-center justify-center mb-3">
                    <Heart className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Dental Practices</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Appointment scheduling, treatment inquiries, and patient communication.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Activity className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Fitness Centers</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Membership inquiries, class scheduling, and trainer appointments.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Calculator className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">Accounting Firms</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Tax appointment scheduling, consultation booking, and client screening.
                  </CardDescription>
                </CardContent>
              </Card>

              <Card className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="w-10 h-10 bg-[#fa4529] text-white rounded-lg flex items-center justify-center mb-3">
                    <Monitor className="w-5 h-5" />
                  </div>
                  <CardTitle className="text-lg text-gray-900">IT Support</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-gray-600 text-sm">
                    Support ticket creation, service requests, and technical consultation scheduling.
                  </CardDescription>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Simple, Usage-Based Pricing
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pay only for what you use. Choose the plan that fits your business needs.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {/* Starter Plan */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-all duration-300 relative">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Starter</CardTitle>
                <CardDescription className="text-gray-600">
                  Perfect for small businesses starting out
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black mb-2">{mockData.pricing.starter.price}</div>
                <div className="text-gray-600 mb-4">per minute</div>
                <div className="text-sm text-gray-500 mb-6">
                  {mockData.pricing.starter.monthlyFee}
                </div>
                <ul className="space-y-2 mb-6">
                  {mockData.pricing.starter.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-gray-500 mb-4">
                  {mockData.pricing.starter.idealFor}
                </div>
                <Button 
                  className="w-full bg-gray-800 text-white hover:bg-gray-900"
                  onClick={handleBookDemo}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>

            {/* Professional Plan */}
            <Card className="border-2 border-[#fa4529] hover:shadow-lg transition-all duration-300 relative">
              <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                <span className="bg-[#fa4529] text-white px-3 py-1 rounded-full text-xs font-semibold">
                  POPULAR
                </span>
              </div>
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Professional</CardTitle>
                <CardDescription className="text-gray-600">
                  Advanced features for growing businesses
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black mb-2">{mockData.pricing.professional.price}</div>
                <div className="text-gray-600 mb-2">per minute</div>
                <div className="text-sm text-green-600 font-semibold mb-2">
                  {mockData.pricing.professional.savings}
                </div>
                <div className="text-sm text-gray-500 mb-6">
                  {mockData.pricing.professional.monthlyFee}
                </div>
                <ul className="space-y-2 mb-6">
                  {mockData.pricing.professional.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-gray-500 mb-4">
                  {mockData.pricing.professional.idealFor}
                </div>
                <Button 
                  className="w-full bg-[#fa4529] text-white hover:bg-[#e03d24]"
                  onClick={handleBookDemo}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>

            {/* Business Plan */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-all duration-300 relative">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Business</CardTitle>
                <CardDescription className="text-gray-600">
                  Full automation with enterprise integrations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-black mb-2">{mockData.pricing.business.price}</div>
                <div className="text-gray-600 mb-2">per minute</div>
                <div className="text-sm text-green-600 font-semibold mb-2">
                  {mockData.pricing.business.savings}
                </div>
                <div className="text-sm text-gray-500 mb-6">
                  {mockData.pricing.business.monthlyFee}
                </div>
                <ul className="space-y-2 mb-6">
                  {mockData.pricing.business.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-gray-500 mb-4">
                  {mockData.pricing.business.idealFor}
                </div>
                <Button 
                  className="w-full bg-gray-800 text-white hover:bg-gray-900"
                  onClick={handleBookDemo}
                >
                  Get Started
                </Button>
              </CardContent>
            </Card>

            {/* Enterprise Plan */}
            <Card className="border-2 border-gray-200 hover:shadow-lg transition-all duration-300 relative">
              <CardHeader>
                <CardTitle className="text-xl text-gray-900">Enterprise</CardTitle>
                <CardDescription className="text-gray-600">
                  Custom solutions for large organizations
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-black mb-2">{mockData.pricing.enterprise.price}</div>
                <div className="text-gray-600 mb-2 text-sm">{mockData.pricing.enterprise.period}</div>
                <div className="text-sm text-green-600 font-semibold mb-2">
                  {mockData.pricing.enterprise.savings}
                </div>
                <div className="text-sm text-gray-500 mb-6">
                  {mockData.pricing.enterprise.monthlyFee}
                </div>
                <ul className="space-y-2 mb-6">
                  {mockData.pricing.enterprise.features.map((feature, index) => (
                    <li key={index} className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                <div className="text-xs text-gray-500 mb-4">
                  {mockData.pricing.enterprise.idealFor}
                </div>
                <Button 
                  className="w-full bg-gray-800 text-white hover:bg-gray-900"
                  onClick={handleBookDemo}
                >
                  Contact Sales
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Additional Pricing Information */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 max-w-3xl mx-auto">
              All plans include our core AI Receptionist features. Choose the tier that matches your business volume and support needs. 
              <br />
              <span className="font-semibold">No setup fees • No long-term contracts • Cancel anytime</span>
            </p>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Our Clients Say
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See how Ghost Engineering's AI Receptionist has transformed businesses like yours.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardHeader>
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <CardTitle className="text-lg text-gray-900">{testimonial.name}</CardTitle>
                  <CardDescription className="text-gray-600">{testimonial.title}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section - Moved above AI Suite */}
      <section className="py-20 bg-black text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">
            Ready to Transform Your Call Handling?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Join hundreds of businesses using Ghost Engineering's AI Receptionist to capture more leads, 
            schedule appointments, and provide exceptional customer service.
          </p>
          <Button size="lg" onClick={handleBookDemo} className="bg-white text-black hover:bg-gray-100 px-8 py-6 text-lg">
            Book a Demo Today
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>

      {/* Ghost Engineering AI Suite Section */}
      <section className="py-20 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center space-x-3 mb-6">
              <img 
                src="https://res.cloudinary.com/do6trhm93/image/upload/v1752670441/ghost-white-ai_hv79iw.png" 
                alt="Ghost AI" 
                className="w-[200px] h-auto"
              />
              <span className="text-4xl font-bold text-white">Suite</span>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              AI Receptionist is just one part of our comprehensive AI automation platform. 
              Discover how Ghost Engineering can transform your entire business with intelligent automation.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <Phone className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">AI Receptionist</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  24/7 call handling, lead capture, and appointment scheduling with intelligent call routing.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-green-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <Zap className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">Workflow Automations</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  Streamline business processes with intelligent automation that connects all your systems.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <MessageCircle className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">AI Chat Agents</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  Intelligent website chatbots that qualify leads, answer questions, and provide 24/7 support.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-orange-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <BarChart3 className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">Social Media Management</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  AI-powered content creation, scheduling, and engagement across all social platforms.
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-red-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <Database className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">Data Integration & Analytics</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  Connect all your business systems with intelligent data processing and actionable insights.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-yellow-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">AI Email Marketing</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  Personalized email campaigns with AI-driven content generation and optimal send timing.
                </CardDescription>
              </CardContent>
            </Card>

            <Card className="bg-gray-800 border-gray-700 hover:bg-gray-750 transition-colors">
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-500 text-white rounded-lg flex items-center justify-center mb-4">
                  <Users className="w-6 h-6" />
                </div>
                <CardTitle className="text-white">Custom AI Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-gray-300">
                  Tailored AI applications designed specifically for your unique business needs and challenges.
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">Ready to Transform Your Business?</h3>
            <p className="text-gray-300 mb-8 max-w-2xl mx-auto">
              Start with AI Receptionist and discover how Ghost Engineering's complete AI automation suite 
              can revolutionize every aspect of your business operations.
            </p>
            <Button size="lg" onClick={handleBookDemo} className="bg-white text-black hover:bg-gray-100 px-8 py-6 text-lg">
              Schedule a Consultation
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <img 
                  src="https://res.cloudinary.com/do6trhm93/image/upload/v1752670441/ghost-white-ai_hv79iw.png" 
                  alt="Ghost AI" 
                  className="w-[120px] h-auto"
                />
              </div>
              <p className="text-gray-400 text-sm">
                AI Receptionist is part of our comprehensive AI automation platform for businesses.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">AI Receptionist</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition-colors">Pricing</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Demo</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">AI Solutions</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white transition-colors">Workflow Automations</a></li>
                <li><a href="#" className="hover:text-white transition-colors">AI Chat Agents</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Social Media Management</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Custom AI Solutions</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="https://www.ghostengr.com/" className="hover:text-white transition-colors">About Ghost Engineering</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
              </ul>
            </div>
          </div>
          <Separator className="my-8 bg-gray-700" />
          <div className="text-center text-gray-400 text-sm">
            © 2024 Ghost Engineering. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 w-12 h-12 bg-gradient-to-r from-[#fa4529] to-[#ff6b3d] text-white rounded-full shadow-lg hover:shadow-xl transform hover:scale-110 transition-all duration-300 z-50 flex items-center justify-center"
          aria-label="Back to top"
        >
          <ArrowRight className="w-5 h-5 rotate-[-90deg]" />
        </button>
      )}
    </div>
  );
};

export default AIReceptionistLanding;